import {
  Dispatch,
  MutableRefObject,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
} from 'react';
import {
  IDetailsListProps,
  merge,
  ColumnActionsMode,
  IColumn,
  ContextualMenu,
  IDetailsList,
} from '@fluentui/react';
import { useColumnSelect, useLocalStorageState } from '@msrceng/component';
import { useColumnSelectToGetDistinctItemList } from 'component/columnSelect';
import { ISearchListColumn } from '../IColumnExtensionType';
import { IOrderBy } from '../types';
import { DownloadProvider } from './download/context';
import { useSearchGroupProps } from './hooks/useSearchGroupProps';
import { useSearchGroupList } from './hooks/useSearchGroupList/useSearchGroupList';
import { useItemFilterFunction } from './SearchFilter';
import { useFilterData } from './SearchFilter/filterContext';
import { ISearchDetailsListProps } from './searchList';
import { SearchListLayout } from './SearchListLayout';
import { DetailsListHeaderAccessibleResizeDialog } from './DetailsListHeaderAccessibleResizeDialog/DetailListHeaderDialog';
import { useDetailsListAccessibleColumnResizeState } from './hooks/useDetailsListAccessibleColumnResizeState';
import { useSearchDetailsListColumnsFromDataColumns } from './hooks/useSearchDetailsListColumnsFromDataColumns';

const SearchListContext = createContext<SearchListData>({} as SearchListData);

export const useSearchListData = <T,>(): SearchListData<T> =>
  useContext(SearchListContext);

/**
 * Provider for the SearchList component.
 */
export const SearchListProvider = <T,>({
  allColumnList,
  bypassFilter = false,
  children,
  defaultColumnKeyList,
  error,
  filterElementList,
  fullPage,
  hideDownload = false,
  hideCommandBar = false,
  hideDatePicker = false,
  itemList = [],
  isLoading = false,
  metadata,
  pageControl,
  detailsListProps: detailsListPropsInOptions = {},
  tableName,
}: PropsWithChildren<ISearchDetailsListProps<T>>) => {
  const [isFilterVisibleInState, setIsFilterVisible] = useLocalStorageState(
    `${tableName}-filterVisible`,
    true
  );

  const isFilterVisible = !bypassFilter && isFilterVisibleInState;

  const [collapsedGroups, setCollapsedGroups] = useLocalStorageState<string[]>(
    `${tableName}-collapsedGroups`,
    []
  );
  const [isAllGroupsCollapsed, setIsAllGroupsCollapsed] = useLocalStorageState(
    `${tableName}-collapsedGroupsAll`,
    false
  );
  const {
    commonFilterState: { orderBy, setOrderBy },
  } = useFilterData();

  const { selectedColumnList } = useColumnSelect<ISearchListColumn>();

  const columns = useSearchDetailsListColumnsFromDataColumns({
    orderBy,
    selectedColumnList,
  });

  const getFilterItemList = useItemFilterFunction();
  const getDistinctItemList = useColumnSelectToGetDistinctItemList();

  // do frontend filtering
  const filteredItemList = bypassFilter
    ? itemList
    : getFilterItemList(itemList);
  const finalItemList = getDistinctItemList(filteredItemList);

  /** Item groupings */
  const groups = useSearchGroupList(
    finalItemList,
    collapsedGroups,
    isAllGroupsCollapsed
  );

  /** A bunch of data about the way groups are handled */
  const groupProps = useSearchGroupProps({
    collapsedGroups,
    setCollapsedGroups,
    isAllGroupsCollapsed,
    setIsAllGroupsCollapsed,
  });

  const sortColumn = (col: IColumn): void => {
    setOrderBy(col?.fieldName || col?.key);
  };

  const {
    isDialogHidden,
    contextualMenuProps,
    columnToEdit,
    setContextualMenuProps,
    getContextualMenuProps,
    detailsListRef,
    hideDialog,
  } = useDetailsListAccessibleColumnResizeState(sortColumn);

  const detailsListProps: Partial<IDetailsListProps> = merge(
    // Do not add the usual {} here. If this doesn't do mutation
    // the entire list refreshes every repaint.
    // Todo: rewrite this
    // {},
    {
      groups,
      groupProps,
      onColumnHeaderClick: (ev, column) => {
        if (
          column &&
          ev &&
          column.columnActionsMode !== ColumnActionsMode.disabled
        ) {
          setContextualMenuProps(getContextualMenuProps(ev, column));
        }
      },
    } as Partial<IDetailsListProps>,
    detailsListPropsInOptions
  );

  const percentLoaded = Math.round(
    (100 * (itemList?.length || 0)) / (pageControl?.rowCount || 1)
  );

  return (
    <SearchListContext.Provider
      value={{
        allColumnList,
        defaultColumnKeyList,
        filterElementList,
        columns,
        itemList: finalItemList,
        percentLoaded,
        isLoading,
        detailsListProps,
        pageControl,
        tableName,
        hideDownload,
        hideCommandBar,
        hideDatePicker,
        error,
        groups,
        orderBy,
        isFilterVisible,
        setIsFilterVisible,
        metadata,
        fullPage,
        detailsListRef,
      }}
    >
      <DownloadProvider<T>
        {...{
          columns,
          items: finalItemList,
          pageControl,
          disabled: !itemList || itemList.length === 0,
          tableName,
        }}
      >
        <SearchListLayout>{children}</SearchListLayout>
        {contextualMenuProps && <ContextualMenu {...contextualMenuProps} />}
        <DetailsListHeaderAccessibleResizeDialog
          isDialogHidden={isDialogHidden}
          hideDialog={hideDialog}
          detailsListRef={detailsListRef}
          columnToEdit={columnToEdit}
        />
      </DownloadProvider>
    </SearchListContext.Provider>
  );
};

export interface SearchListData<T = any>
  extends Pick<
    ISearchDetailsListProps<T>,
    | 'error'
    | 'defaultColumnKeyList'
    | 'allColumnList'
    | 'defaultColumnKeyList'
    | 'allColumnList'
    | 'itemList'
    | 'isLoading'
    | 'detailsListProps'
    | 'pageControl'
    | 'filterElementList'
    | 'tableName'
    | 'hideDownload'
    | 'hideCommandBar'
    | 'hideDatePicker'
    | 'metadata'
    | 'fullPage'
  > {
  /** The active column list (after column selection transforms) */
  columns: ISearchListColumn[];
  /** Compiled grouping output */
  groups?: any[];
  /** Sort order info */
  orderBy?: IOrderBy;
  /** How much of the data is loaded */
  percentLoaded: number;
  /** Getter for whether the filter bar is shown/hidden */
  isFilterVisible?: boolean;
  /** Setter for whether the filter bar is shown/hidden */
  setIsFilterVisible: Dispatch<SetStateAction<boolean>>;
  detailsListRef: MutableRefObject<IDetailsList | null>;
}
